// hooks/use-playbook-items.js — LEAN PLAYBOOK CRUD HOOK
// ═══════════════════════════════════════════════════════════
// Self-contained hook for the Playbook feature. Wraps the live
// /api/playbook endpoint (list + delete + drop + create) without
// the heavy template-creation state surface the v1 hook carried.
// Drop-in for features/playbook-feature.js, shaped the same way
// as hooks/use-notes.js.
//
// Template creation, community submission, review — deferred. Live
// /api/playbook supports them; wire when the UI's ready.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';

// Taxonomy — 4 top-level categories. Each has subtypes the AI or
// the user can tag. Color mapping stays within Shift's 4-color
// palette where possible; Frames uses neutral blue-gray as a 5th
// utility since it doesn't map to any core meaning.
export const PLAYBOOK_CATEGORIES = {
  lenses: { label: 'Lenses', color: '#9b8fc4', types: ['mental_model', 'framework', 'heuristic', 'principle', 'decision_matrix', 'snippet', 'prompt'] },
  plays:  { label: 'Plays',  color: '#c8a96e', types: ['template', 'process', 'sop', 'workflow', 'sequence', 'checklist', 'routine', 'algorithm', 'pipeline', 'protocol', 'method', 'function', 'lifecycle'] },
  reads:  { label: 'Reads',  color: '#7a9e82', types: ['cheatsheet', 'diagnostic', 'rubric', 'benchmark', 'anti_pattern', 'trigger', 'exit_criteria', 'scenario', 'calibration'] },
  frames: { label: 'Frames', color: '#6b8fa8', types: ['structure', 'taxonomy', 'layout', 'constraint_set', 'flowchart', 'formula', 'blueprint'] },
};

// Reverse lookup: subtype → top-level category
export function getCategoryForType(itemType) {
  for (const [cat, def] of Object.entries(PLAYBOOK_CATEGORIES)) {
    if (def.types.includes(itemType)) return cat;
  }
  return 'plays';
}

export function usePlaybookItems({ userId, supabase }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token || ''}`,
    };
  }, [supabase]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const [personalRes, communityRes] = await Promise.all([
        fetch('/api/playbook', {
          method: 'POST', headers,
          body: JSON.stringify({ action: 'list', userId, visibility: 'all' }),
        }).then(r => r.ok ? r.json() : { items: [] }).catch(() => ({ items: [] })),
        fetch('/api/playbook', {
          method: 'POST', headers,
          body: JSON.stringify({ action: 'community', limit: 50 }),
        }).then(r => r.ok ? r.json() : { items: [] }).catch(() => ({ items: [] })),
      ]);
      const personal = personalRes.items || [];
      const community = communityRes.items || [];
      const personalIds = new Set(personal.map(i => i.id));
      const communityOnly = community.filter(i => !personalIds.has(i.id)).map(i => ({ ...i, _fromCommunity: true }));
      setItems([...personal, ...communityOnly]);
    } catch (e) {
      setError(e.message || 'Failed to load playbook');
    } finally {
      setLoading(false);
    }
  }, [userId, authHeaders]);

  useEffect(() => { if (userId) load(); }, [userId, load]);

  async function deleteItem(itemId) {
    setItems(prev => prev.filter(i => i.id !== itemId));
    try {
      const headers = await authHeaders();
      await fetch('/api/playbook', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'delete', itemId, userId }),
      });
    } catch (e) { console.error('deleteItem:', e); }
  }

  async function dropItem(item, currentSessionId) {
    if (!item || !currentSessionId) return;
    try {
      const headers = await authHeaders();
      await fetch('/api/playbook', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'drop', userId, sessionId: currentSessionId, playbookItemId: item.id }),
      });
      setItems(prev => prev.map(p => p.id === item.id
        ? { ...p, use_count: (p.use_count || 0) + 1, last_used_at: new Date().toISOString() }
        : p));
    } catch (e) { console.error('dropItem:', e); }
  }

  async function createItem({ itemType, title, content, contextTags = [] }) {
    if (!title?.trim() || !content) return null;
    try {
      const headers = await authHeaders();
      const res = await fetch('/api/playbook', {
        method: 'POST', headers,
        body: JSON.stringify({
          action: 'create', userId, itemType,
          title: title.trim(), content, contextTags,
        }),
      });
      if (!res.ok) return null;
      const { item } = await res.json();
      if (item) setItems(prev => [item, ...prev]);
      return item;
    } catch (e) { console.error('createItem:', e); return null; }
  }

  return {
    items, loading, error,
    refresh: load,
    deleteItem, dropItem, createItem,
  };
}

// Flatten content into a plain-text string for previews / card bodies.
// Playbook items can have very different content shapes (snippet = { text },
// prompt = { prompt_text }, template = { blocks: [...] }, cheatsheet =
// { sections: [...] }, etc.). This pulls whatever reads as text.
export function extractPlaybookText(item, maxLen = 160) {
  const c = item?.content;
  if (!c) return '';
  let text = '';
  if (typeof c === 'string') {
    text = c;
  } else if (typeof c === 'object') {
    text = c.purpose || c.use_case || c.prompt_text || c.description
        || c.summary || c.overview || c.instruction
        || (Array.isArray(c.sections) ? c.sections.map(s => s.heading || s.title || s.label || '').filter(Boolean).join(' · ') : '')
        || (Array.isArray(c.steps) ? c.steps.map(s => s.value || s.label || s.title || '').filter(Boolean).join(' → ') : '')
        || (Array.isArray(c.blocks) ? c.blocks.map(b => b.heading || b.title || '').filter(Boolean).join(' · ') : '')
        || c.structure || c.text || '';
  }
  text = String(text)
    .replace(/```[\s\S]*?```/g, '')
    .replace(/[*_`#>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length <= maxLen) return text;
  const cut = text.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > maxLen * 0.6 ? cut.slice(0, lastSpace) : cut) + '…';
}
